import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './SplashScreen.css';
import Footer from '../Footer/Footer';
import HeroBackgroundRotator from './HeroBackgroundRotator';
import ScrollTextRotator from './ScrollTextRotator';
import Seo from '../SEO/Seo';
import pharmaLogo from '../../assets/logopharmagreen.png';

const HERO_POSTERS = [
  {
    src: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?auto=format&fit=crop&w=900&q=80',
    alt: 'Medical students in white coats studying together',
  },
  {
    src: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=900&q=80',
    alt: 'Medical student reviewing notes with tablet',
  },
  {
    src: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=900&q=80',
    alt: 'Medical students collaborating for exams',
  },
];

const SplashScreen = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="splash-screen">
      <Seo
        title="Medical Learning Platform"
        description="Prepare for End of Semester, Licensing, and CAT 1/CAT 2 exams with study materials organized by programme, module, and topic, plus self-evaluation tests."
        path="/"
        keywords={[
          'end of semester exam preparation',
          'licensing exam preparation',
          'CAT 1 exam preparation',
          'CAT 2 exam preparation',
          'medical study notes',
          'programme module topic learning',
          'self evaluation tests',
          'ALIEN GREEN',
        ]}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'ALIEN GREEN (EduTechnologies)',
          url: process.env.REACT_APP_SITE_URL || 'https://aliengreen.pharmagree.co.tz',
          description: 'Medical learning platform for End of Semester, Licensing, and CAT 1/CAT 2 exam preparation with organized study materials and self-evaluation tests.',
        }}
      />
      <HeroBackgroundRotator />
      <div className="splash-container">
        {/* Navigation Bar */}
        <nav className="splash-nav">
          <div className="splash-nav-container">
            <Link to="/" className="splash-nav-brand">
              <img
                src={pharmaLogo}
                alt="ALIEN GREEN (EduTechnologies) Logo"
                className="splash-nav-brand-logo"
              />
              <span>
                ALIEN GREEN <span className="brand-parenthetical">(EduTechnologies)</span>
              </span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex splash-nav-links">
              <Link to="/" className="splash-nav-link">Home</Link>
              <Link to="/about" className="splash-nav-link">About</Link>
              <Link to="/services" className="splash-nav-link">Services</Link>
              <Link to="/programmes" className="splash-nav-link">Programmes</Link>
              <Link to="/events" className="splash-nav-link splash-nav-link-with-badge">
                <span>Events &amp; Updates</span>
                <span className="splash-nav-new-badge">NEW</span>
              </Link>
              <Link to="/login" className="splash-nav-link">Login</Link>
              <Link to="/register" className="splash-nav-link splash-nav-link-primary">Sign Up</Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg hover:bg-white/10 transition-colors"
              aria-label="Toggle menu"
            >
              <span className={`block w-6 h-0.5 bg-white transition-all ${mobileMenuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-white transition-all ${mobileMenuOpen ? 'opacity-0' : ''}`}></span>
              <span className={`block w-6 h-0.5 bg-white transition-all ${mobileMenuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-white/20 bg-black/30 backdrop-blur-md">
              <div className="flex flex-col px-4 py-4 space-y-3">
                <Link 
                  to="/" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-white/10 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Home
                </Link>
                <Link 
                  to="/about" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-white/10 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  About
                </Link>
                <Link 
                  to="/services" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-white/10 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Services
                </Link>
                <Link 
                  to="/programmes" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-white/10 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Programmes
                </Link>
                <Link
                  to="/events"
                  className="splash-nav-link splash-nav-link-with-badge px-3 py-2 rounded-lg hover:bg-white/10 transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span>Events &amp; Updates</span>
                  <span className="splash-nav-new-badge">NEW</span>
                </Link>
                <Link 
                  to="/login" 
                  className="splash-nav-link px-3 py-2 rounded-lg hover:bg-white/10 transition-colors" 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="splash-nav-link-primary px-3 py-2 text-center rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign Up
                </Link>
              </div>
            </div>
          )}
        </nav>

        {/* Background orbs removed so the rotating background images are clearly visible */}

        {/* Main Content */}
        <div className="splash-main">
          <div className="splash-content">
            {/* Logo/Icon */}
            <div className="splash-logo splash-animate-float">
              <img 
                src={pharmaLogo} 
                alt="Pharma Green Logo" 
                className="logo-svg"
              />
            </div>

            <h1 className="splash-eyebrow splash-eyebrow-main splash-animate-in splash-delay-1">
              Your Medical Education Platform
            </h1>
            <p className="splash-subheadline splash-animate-in splash-delay-2">
              For medical students and professionals who need high-quality, well-organized
              notes, past papers and revision papers, practice questions for CAT 1 and
              CAT 2, End of Semester exam preparation, and strong preparation for
              licensing examinations. All materials are for learning and revision only.
            </p>

            <div className="splash-poster-grid splash-animate-in splash-delay-2">
              {HERO_POSTERS.map((poster) => (
                <article key={poster.src} className="splash-poster-card">
                  <img src={poster.src} alt={poster.alt} loading="lazy" />
                </article>
              ))}
            </div>
            <div className="splash-animate-in splash-delay-2">
              <ScrollTextRotator stepPx={240} />
            </div>

            {/* Navigation Buttons */}
            <div className="splash-buttons splash-animate-in splash-delay-4">
              <Link to="/login" className="splash-button splash-button-primary">
                <span>Get Started</span>
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </Link>
              <Link to="/programmes" className="splash-button splash-button-secondary">
                <span>Explore Programmes</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="splash-footer-wrapper">
          <Footer isSplash={true} />
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
